/**
 * Paginação numerada (‹ 1 2 … N ›) — nasceu na tela de clientes e é a mesma
 * no extrato de apontamentos e na folha de horas. Controlada: a tela guarda a
 * página (na URL, como clientes) e decide o que o clique faz.
 */
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';

/** Até 7 páginas, todas; acima, 1 … a atual e as vizinhas … N. */
export function pageItems(current: number, count: number): (number | '…')[] {
  if (count <= 7) return Array.from({ length: count }, (_, i) => i + 1);
  const pages = new Set<number>([1, count, current - 1, current, current + 1]);
  const sorted = [...pages].filter((p) => p >= 1 && p <= count).sort((a, b) => a - b);
  const items: (number | '…')[] = [];
  let prev = 0;
  for (const p of sorted) {
    if (p - prev > 1) items.push('…');
    items.push(p);
    prev = p;
  }
  return items;
}

/** Quantas páginas `total` itens ocupam — no mínimo 1 ("página 1 de 1" com a lista vazia). */
export function pageCountOf(total: number, pageSize: number): number {
  return Math.max(1, Math.ceil(total / pageSize));
}

export function Pagination({
  page,
  pageCount,
  onPage,
}: {
  page: number;
  pageCount: number;
  onPage: (page: number) => void;
}) {
  const base =
    'flex size-8 items-center justify-center rounded-md border text-[13px] tabular-nums transition-colors';
  return (
    <nav className="flex items-center gap-1" aria-label="Paginação">
      <button
        type="button"
        aria-label="Página anterior"
        disabled={page <= 1}
        onClick={() => onPage(page - 1)}
        className={cn(base, 'border-transparent hover:bg-accent disabled:pointer-events-none disabled:opacity-40')}
      >
        <ChevronLeft className="size-4" />
      </button>
      {pageItems(page, pageCount).map((item, i) =>
        item === '…' ? (
          <span key={`e${i}`} className="px-1 text-muted-foreground">
            …
          </span>
        ) : (
          <button
            key={item}
            type="button"
            aria-current={item === page ? 'page' : undefined}
            onClick={() => onPage(item)}
            className={cn(
              base,
              item === page
                ? 'border-primary font-medium text-primary'
                : 'border-transparent hover:bg-accent'
            )}
          >
            {item}
          </button>
        )
      )}
      <button
        type="button"
        aria-label="Próxima página"
        disabled={page >= pageCount}
        onClick={() => onPage(page + 1)}
        className={cn(base, 'border-transparent hover:bg-accent disabled:pointer-events-none disabled:opacity-40')}
      >
        <ChevronRight className="size-4" />
      </button>
    </nav>
  );
}
